/**
 * MarketingContentPage — Phase 32.9.
 *
 * Full-page content viewer and markdown editor for a single MarketingContent
 * row. Fetches by content ID, shows structured preview for campaign_output
 * type, provides a raw-body textarea editor, and saves via founder-signed
 * PATCH /admin/marketing/content/{id}.
 */

import { useState, useEffect } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { apiFetch, apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ContentDetail {
  id: string;
  campaign_id: string;
  content_type: string;
  body: string;
  brand_voice_version: string;
  llm_response_hash: string | null;
  created_at: number;
}

// ── API ───────────────────────────────────────────────────────────────────────

function fetchContent(id: string): Promise<ContentDetail> {
  return apiFetch<ContentDetail>(`/admin/marketing/content/${id}`);
}

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function saveContent(contentId: string, body: string): Promise<void> {
  const reqBody = { nonce: crypto.randomUUID(), body };
  const canonical = JSON.stringify(sortKeys(reqBody));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/admin/marketing/content/${contentId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Founder-Signature': sig,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: canonical,
  });
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function parseCampaignOutput(body: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(body) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: mono ? '8.5pt' : '10pt', color: 'var(--omega-ink)', fontFamily: mono ? 'monospace' : 'inherit', lineHeight: 1.5 }}>
        {value}
      </div>
    </div>
  );
}

function KeywordChips({ keywords }: { keywords: unknown }) {
  if (!Array.isArray(keywords) || keywords.length === 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        Keywords
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {(keywords as unknown[]).map((k, i) => (
          <span
            key={i}
            style={{
              background: 'rgba(125,211,252,0.1)',
              border: '1px solid rgba(125,211,252,0.25)',
              borderRadius: 3,
              color: '#7dd3fc',
              fontSize: '8pt',
              padding: '2px 7px',
            }}
          >
            {String(k)}
          </span>
        ))}
      </div>
    </div>
  );
}

function MarkdownSection({ title, content }: { title: string; content: unknown }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--omega-ink)',
          fontFamily: 'var(--omega-mono)',
          fontSize: '8.5pt',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '6px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          textAlign: 'left',
        }}
      >
        <span>{open ? '▾' : '▸'}</span>
        {title}
      </button>
      {open && (
        <pre
          style={{
            fontSize: '8.5pt',
            color: 'var(--omega-ink)',
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 4,
            padding: '12px 14px',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.6,
            marginTop: 4,
          }}
        >
          {String(content)}
        </pre>
      )}
    </div>
  );
}

function StructuredPreview({ body }: { body: string }) {
  const parsed = parseCampaignOutput(body);
  if (!parsed) {
    return (
      <pre style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', background: 'rgba(0,0,0,0.3)', borderRadius: 4, padding: '14px 16px', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
        {body}
      </pre>
    );
  }

  return (
    <div>
      {/* Hero */}
      {!!parsed.hero_h1 && <FieldRow label="Hero H1" value={String(parsed.hero_h1)} />}
      {!!parsed.hero_subtitle && <FieldRow label="Hero Subtitle" value={String(parsed.hero_subtitle)} />}

      {/* SEO */}
      {!!parsed.title_tag && <FieldRow label="Title Tag" value={String(parsed.title_tag)} />}
      {!!parsed.meta_description && <FieldRow label="Meta Description" value={String(parsed.meta_description)} />}
      <KeywordChips keywords={parsed.keywords} />

      {/* Pricing */}
      {parsed.price_usd_cents !== undefined && (
        <FieldRow
          label="Pricing"
          value={`$${((parsed.price_usd_cents as number) / 100).toFixed(2)} / mo${parsed.tier_name != null ? ` · ${String(parsed.tier_name)}` : ''}`}
        />
      )}

      {/* Long-form markdown */}
      <MarkdownSection title="Landing Page" content={parsed.landing_page_md} />
      <MarkdownSection title="Blog Post" content={parsed.blog_post_md} />
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────

type Tab = 'preview' | 'edit';

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tab = (t: Tab, label: string) => (
    <button
      key={t}
      onClick={() => onChange(t)}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: `2px solid ${active === t ? '#7dd3fc' : 'transparent'}`,
        color: active === t ? '#7dd3fc' : 'var(--omega-ink-subtle)',
        fontFamily: 'var(--omega-mono)',
        fontSize: '9pt',
        fontWeight: active === t ? 700 : 400,
        padding: '6px 16px 8px',
        cursor: 'pointer',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 16 }}>
      {tab('preview', 'Preview')}
      {tab('edit', 'Edit Source')}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MarketingContentPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('preview');
  const [editValue, setEditValue] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'content', id],
    queryFn: () => fetchContent(id),
    enabled: !!id,
  });

  // Populate editor when data loads.
  useEffect(() => {
    if (data) setEditValue(data.body);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => saveContent(id, editValue),
    onSuccess: () => {
      setSaved(true);
      setSaveError(null);
      void qc.invalidateQueries({ queryKey: ['marketing', 'content', id] });
      void qc.invalidateQueries({ queryKey: ['marketing', 'campaign', data?.campaign_id] });
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  const isDirty = data ? editValue !== data.body : false;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px', maxWidth: 860 }}
    >
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Campaigns', to: '/omega/marketing/campaigns' },
          ...(data
            ? [
                { label: data.campaign_id.slice(0, 8), to: `/omega/marketing/campaigns/${data.campaign_id}` },
                { label: data.content_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
              ]
            : [{ label: id.slice(0, 8) }]),
        ]}
      />

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', marginTop: 20 }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 20 }}>Failed to load content.</div>
      )}

      {data && (
        <>
          {/* Header */}
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.04em' }}>
                {data.content_type}
              </h1>
              <span
                style={{
                  fontSize: '8pt',
                  padding: '1px 7px',
                  background: 'rgba(125,211,252,0.1)',
                  border: '1px solid rgba(125,211,252,0.3)',
                  borderRadius: 3,
                  color: '#7dd3fc',
                  letterSpacing: '0.05em',
                }}
              >
                v{data.brand_voice_version}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
              <span>Created {fmtTime(data.created_at)}</span>
              {data.llm_response_hash && (
                <span>
                  LLM hash:{' '}
                  <code style={{ fontSize: '8pt', color: 'var(--omega-ink)' }}>
                    {data.llm_response_hash.slice(0, 12)}…
                  </code>
                </span>
              )}
              <Link
                to={`/omega/marketing/campaigns/${data.campaign_id}`}
                style={{ color: '#7dd3fc', textDecoration: 'none' }}
              >
                ← Campaign
              </Link>
            </div>
          </div>

          {/* Tab bar */}
          <TabBar active={tab} onChange={setTab} />

          {/* Preview tab */}
          {tab === 'preview' && (
            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6,
                padding: '16px 18px',
              }}
            >
              <StructuredPreview body={editValue} />
            </div>
          )}

          {/* Edit tab */}
          {tab === 'edit' && (
            <div>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginBottom: 8 }}>
                Editing the raw body. For{' '}
                <code style={{ fontSize: '8pt' }}>campaign_output</code> type this is a JSON blob.
                Your edits create a new signed version; the original is preserved in the audit log.
              </div>
              <textarea
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: 420,
                  background: 'rgba(0,0,0,0.35)',
                  border: `1px solid ${isDirty ? 'rgba(125,211,252,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 4,
                  color: 'var(--omega-ink)',
                  fontFamily: 'monospace',
                  fontSize: '8.5pt',
                  lineHeight: 1.6,
                  padding: '12px 14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />

              {saveError && (
                <div style={{ color: '#f87171', fontSize: '8.5pt', marginTop: 6 }}>{saveError}</div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center' }}>
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || !isDirty}
                  style={{
                    background: saved ? '#6ee7b7' : '#7dd3fc',
                    border: 'none',
                    borderRadius: 4,
                    color: '#000',
                    fontFamily: 'var(--omega-mono)',
                    fontSize: '9pt',
                    fontWeight: 700,
                    padding: '7px 18px',
                    cursor: saveMutation.isPending || !isDirty ? 'not-allowed' : 'pointer',
                    opacity: saveMutation.isPending || !isDirty ? 0.55 : 1,
                    transition: 'background 0.2s',
                  }}
                >
                  {saveMutation.isPending
                    ? 'Signing & Saving…'
                    : saved
                    ? 'Saved'
                    : 'Save Changes'}
                </button>
                {isDirty && (
                  <button
                    onClick={() => { setEditValue(data.body); setSaveError(null); }}
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 4,
                      color: 'var(--omega-ink-subtle)',
                      fontFamily: 'var(--omega-mono)',
                      fontSize: '9pt',
                      padding: '7px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    Discard
                  </button>
                )}
                {isDirty && (
                  <span style={{ fontSize: '8pt', color: '#fbbf24', marginLeft: 4 }}>
                    Unsaved changes
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
